/**
 * src/pages/CriticalThinking/data/claims.data.ts
 * 6 Fictional claims for the Claim Analyzer tool.
 * Evaluated across the 8-step framework with explicit expert rationale,
 * claim classification, and epistemically honest verdicts.
 */

export type ClaimType = "factual" | "interpretive" | "predictive" | "normative";

export interface ClaimStepOption {
  id: string;
  label: string;
  explanation: string;
}

export interface ClaimCase {
  id: string;
  title: string;
  scenario: string;
  quote: string;
  sourceContext: string;
  claimType: ClaimType;
  typeExplanation: string;
  steps: {
    // 1. What exactly is asserted
    assertionOptions: ClaimStepOption[];
    correctAssertionId: string;
    // 2. What would make it true or false
    truthConditions: ClaimStepOption[];
    correctTruthConditionId: string;
    // 3. Evidence offered
    evidenceOffered: ClaimStepOption[];
    correctEvidenceId: string;
    // 4. Evidence reliability
    reliabilityOptions: ClaimStepOption[];
    correctReliabilityId: string;
    // 5. Assumptions required
    assumptionsOptions: ClaimStepOption[];
    correctAssumptionId: string;
    // 6. Alternative explanations
    alternativesOptions: ClaimStepOption[];
    correctAlternativeId: string;
    // 7. What would weaken it
    weakenersOptions: ClaimStepOption[];
    correctWeakenerId: string;
    // 8. Verdict
    verdictOptions: {
      id: "well-supported" | "plausible-unestablished" | "not-established" | "insufficient-info" | "contradicted";
      label: string;
      rationale: string;
    }[];
    correctVerdictId: "well-supported" | "plausible-unestablished" | "not-established" | "insufficient-info" | "contradicted";
  };
  expertSynthesis: {
    summary: string;
    whyInsufficientInfoOrNotEstablished: string;
    plausibleAlternativesDiscussion: string;
  };
}

export const CLAIM_CASES: ClaimCase[] = [
  {
    id: "cafe-focus",
    title: "The Artisanal Cafe Focus Boost",
    scenario: "A newly opened neighbourhood roastery displays a prominent chalkboard counter claiming their proprietary roast provides a measured cognitive lift.",
    quote: "\"Our Cold-Pressed Northern Roast boosts focus and mental stamina by 40% throughout the entire workday!\"",
    sourceContext: "Promotional marketing chalk board inside 'Aura Brew Co.', accompanied by a small print note referencing 'an internal trial of 12 regular staff members'.",
    claimType: "factual",
    typeExplanation: "It makes a concrete, quantified descriptive claim about physical and psychological cognitive performance that is in principle measurable via psychometric testing.",
    steps: {
      assertionOptions: [
        {
          id: "a1",
          label: "Any person drinking any coffee will gain exactly 40% higher test scores.",
          explanation: "Overgeneralised: the claim specifies their particular Cold-Pressed Northern Roast, not all generic coffees."
        },
        {
          id: "a2",
          label: "Drinking this specific roast causes a measurable 40% enhancement in sustained focus and stamina across an 8-hour period for typical consumers.",
          explanation: "Accurate: this captures both the specific product, the asserted magnitude (+40%), and the claimed duration (workday)."
        },
        {
          id: "a3",
          label: "Customers report feeling somewhat more awake after drinking caffeine.",
          explanation: "Underspecified: the commercial claim asserts a precise quantitative 40% boost and stamina, not merely subjective alertness."
        }
      ],
      correctAssertionId: "a2",

      truthConditions: [
        {
          id: "t1",
          label: "Rigorous double-blind, placebo-controlled trials showing a statistically significant ~40% increase on validated attention batteries versus an active decaf baseline across a representative group.",
          explanation: "This directly tests the causal claim while controlling for placebo effects, caffeine tolerance, and expectation bias."
        },
        {
          id: "t2",
          label: "Whether customers leave positive reviews on social media about feeling productive.",
          explanation: "Customer reviews measure satisfaction and expectation, not objective cognitive endurance or baseline improvement."
        },
        {
          id: "t3",
          label: "Checking whether the coffee beans contain caffeine and chlorogenic acid.",
          explanation: "Chemical presence is necessary for pharmacological action, but does not prove a 40% focus boost over an entire workday."
        }
      ],
      correctTruthConditionId: "t1",

      evidenceOffered: [
        {
          id: "e1",
          label: "An unblinded, self-reported trial of 12 cafe staff members who knew they were testing their own company's flagship product.",
          explanation: "The cafe openly cites an internal trial of 12 regular staff members."
        },
        {
          id: "e2",
          label: "Peer-reviewed neuroimaging data comparing prefrontal cortex activation against modafinil.",
          explanation: "No such formal academic study was conducted or presented."
        },
        {
          id: "e3",
          label: "National barista competition judging scores.",
          explanation: "Barista judging scores taste, crema, and extraction, not consumer cognitive performance."
        }
      ],
      correctEvidenceId: "e1",

      reliabilityOptions: [
        {
          id: "r1",
          label: "Extremely low: high conflict of interest, no control group, tiny sample (n=12), self-report, prone to placebo effect and demand characteristics.",
          explanation: "Staff testing their own employer's coffee have powerful social incentives and expectation biases."
        },
        {
          id: "r2",
          label: "Moderate: 12 people is enough to establish a basic trend for everyday grocery products.",
          explanation: "12 unblinded people with financial and reputational loyalty cannot establish a scientific percentage lift."
        },
        {
          id: "r3",
          label: "High: baristas are coffee experts who understand taste and bodily reaction better than non-specialists.",
          explanation: "Sensory expertise in cupping does not overcome subjective bias, placebo response, or lack of objective metric controls."
        }
      ],
      correctReliabilityId: "r1",

      assumptionsOptions: [
        {
          id: "m1",
          label: "Assuming staff reports accurately track objective neural performance, that 'focus' is a single uniform metric, and that the effect persists for 8 hours without an afternoon crash.",
          explanation: "Identifies the core unstated assumptions bridging subjective feelings to an objective quantitative claim."
        },
        {
          id: "m2",
          label: "Assuming all cafe patrons drink black coffee without milk or sugar.",
          explanation: "Additive preferences might modulate absorption slightly, but is not the core epistemic assumption underlying the 40% figure."
        },
        {
          id: "m3",
          label: "Assuming the cafe's roasting machine was calibrated on a Tuesday.",
          explanation: "Irrelevant operational detail."
        }
      ],
      correctAssumptionId: "m1",

      alternativesOptions: [
        {
          id: "l1",
          label: "Placebo effect, confirmation bias, staff eagerness to validate the roast, natural fluctuations in daily energy, or basic non-specific caffeine stimulation equivalent to any ordinary tea or instant coffee.",
          explanation: "Captures the standard psychological and physiological confounding variables."
        },
        {
          id: "l2",
          label: "The cafe secretly added prescription stimulants to the beverage without disclosing ingredients.",
          explanation: "Conspiratorial and unparsimonious without specific toxicological evidence."
        },
        {
          id: "l3",
          label: "The staff members were all meditating before drinking the coffee.",
          explanation: "Unsubstantiated arbitrary speculation."
        }
      ],
      correctAlternativeId: "l1",

      weakenersOptions: [
        {
          id: "w1",
          label: "An independent blinded test on 100 students showing identical attention scores between this roast and ordinary drip coffee.",
          explanation: "Directly refutes the claim of a unique 40% enhancement over typical baseline."
        },
        {
          id: "w2",
          label: "The cafe owner deciding to lower the price by fifty cents.",
          explanation: "Pricing has no bearing on pharmacological cognitive efficacy."
        },
        {
          id: "w3",
          label: "One customer complaining that the cup was lukewarm.",
          explanation: "An isolated service defect does not address the scientific validity of the focus claim."
        }
      ],
      correctWeakenerId: "w1",

      verdictOptions: [
        {
          id: "well-supported",
          label: "Well supported",
          rationale: "Requires robust, replicated, blinded empirical evidence, which is entirely absent here."
        },
        {
          id: "plausible-unestablished",
          label: "Plausible but unestablished",
          rationale: "While mild caffeine stimulation is plausible, a precise 40% workday-long cognitive leap attributed to this roast is far from established."
        },
        {
          id: "not-established",
          label: "Not established",
          rationale: "The evidence offered (12 unblinded employees) is methodologically incapable of justifying a quantified 40% physiological claim."
        },
        {
          id: "insufficient-info",
          label: "Insufficient information",
          rationale: "While we lack formal lab assays, we have enough information about the offered trial to conclude the marketing claim is unestablished."
        },
        {
          id: "contradicted",
          label: "Contradicted by evidence",
          rationale: "To declare it positively contradicted would require an existing negative trial on this specific blend."
        }
      ],
      correctVerdictId: "not-established"
    },
    expertSynthesis: {
      summary: "The cafe makes an extraordinary quantitative claim ('40% focus boost across the workday') supported solely by an unblinded, non-placebo trial on 12 staff members with an inherent conflict of interest.",
      whyInsufficientInfoOrNotEstablished: "Notice the difference between saying 'the coffee definitely does not help' and 'the claim is not established'. Critical thinking avoids declaring the roast useless without a trial; rather, we recognize that the burden of proof rests on the cafe, and their anecdotal, biased evidence fails to meet it.",
      plausibleAlternativesDiscussion: "Anyone drinking caffeine experiences adenosine antagonism. A generic 80mg caffeine dose will naturally boost alertness in non-habituated individuals; attributing a massive 40% daily lift to the roaster's artisanal method conflates standard caffeine effect with a proprietary wonder-drug."
    }
  },

  {
    id: "town-factory",
    title: "The Industrial Facility & Valley Illness Cluster",
    scenario: "In the small river town of Oakhaven, local clinic visits for respiratory irritation doubled over a six-month period following the opening of an advanced composite polymer plant.",
    quote: "\"Clinic visits for acute respiratory complaints increased by 115% in the six months since Polymerix opened; the factory emissions are undeniably poisoning the town.\"",
    sourceContext: "An impassioned flyer distributed by an Oakhaven resident advocacy committee at a town council hearing.",
    claimType: "factual",
    typeExplanation: "It links an observed statistical trend (clinic visits) directly to a specific physical cause (chemical poisoning from factory emissions).",
    steps: {
      assertionOptions: [
        {
          id: "a1",
          label: "Every resident of Oakhaven has developed severe lung damage from breathing toxic fumes.",
          explanation: "Exaggerated: the flyer states clinic visits doubled, not universal catastrophic lung destruction."
        },
        {
          id: "a2",
          label: "The factory's airborne emissions are the direct causal driver of the 115% increase in clinic visits for respiratory issues.",
          explanation: "Accurate: the quote asserts a definitive causal connection ('undeniably poisoning') based on the temporal correlation."
        },
        {
          id: "a3",
          label: "The factory owner failed to pay local municipal property taxes.",
          explanation: "Completely unrelated to the respiratory and environmental claim."
        }
      ],
      correctAssertionId: "a2",

      truthConditions: [
        {
          id: "t1",
          label: "Demonstrated toxic emissions above safe thresholds matching patient biomarkers, an epidemiological gradient (closer residents having higher incidence), and ruling out seasonal viral outbreaks or pollen spikes.",
          explanation: "Rigorous environmental epidemiology requires exposure pathways, dose-response gradients, and control for confounding seasonal illness."
        },
        {
          id: "t2",
          label: "Proving that the factory manager lives in another state.",
          explanation: "Residency of leadership has no bearing on chemical toxicology or emission dispersal patterns."
        },
        {
          id: "t3",
          label: "Counting the number of signatures on the advocacy committee's online petition.",
          explanation: "Public concern proves social alarm, but cannot establish toxicological causation."
        }
      ],
      correctTruthConditionId: "t1",

      evidenceOffered: [
        {
          id: "e1",
          label: "A temporal correlation: clinic visit counts for respiratory symptoms rose 115% over the same six-month window after the plant commenced operations.",
          explanation: "The evidence consists purely of timing (post hoc ergo propter hoc correlation)."
        },
        {
          id: "e2",
          label: "Continuous ambient air quality mass-spectrometry logs and blood serum toxicology from 400 affected patients.",
          explanation: "No environmental sampling or toxicology assays were presented in the flyer."
        },
        {
          id: "e3",
          label: "A signed confession from the factory safety supervisor.",
          explanation: "No confession exists."
        }
      ],
      correctEvidenceId: "e1",

      reliabilityOptions: [
        {
          id: "r1",
          label: "The clinic count data may be genuine factual records, but using temporal correlation alone to assert direct chemical causation has very low evidentiary validity.",
          explanation: "Accurately separates the credibility of the raw count from the leap to causal attribution."
        },
        {
          id: "r2",
          label: "Total fraud: the clinic numbers were undoubtedly fabricated by activists.",
          explanation: "Cynical assumption without evidence; clinic visits could very well have doubled for other reasons."
        },
        {
          id: "r3",
          label: "100% decisive: whenever an event follows an industrial opening, modern science attributes the outcome to the new facility.",
          explanation: "Classic post-hoc fallacy that ignores seasonal illness, pollen, reporting bias, and diagnostic shifts."
        }
      ],
      correctReliabilityId: "r1",

      assumptionsOptions: [
        {
          id: "m1",
          label: "Assuming that no other factors changed over those six months (e.g. wildfire smoke, severe influenza season, mold bloom, increased clinic staffing, or heightened diagnostic reporting due to community anxiety).",
          explanation: "Highlights the unstated assumption that temporal co-occurrence implies singular causal exclusivity."
        },
        {
          id: "m2",
          label: "Assuming the factory produces plastic toys instead of industrial seals.",
          explanation: "Irrelevant product categorization."
        },
        {
          id: "m3",
          label: "Assuming doctors never take vacations in winter.",
          explanation: "Trivial operational detail."
        }
      ],
      correctAssumptionId: "m1",

      alternativesOptions: [
        {
          id: "l1",
          label: "A severe regional influenza/RSV wave, record pollen/mold counts, cross-border wildfire drift, or increased screening awareness where residents sought care for mild symptoms they previously weathered at home.",
          explanation: "Plausible, documented epidemiological confounders that frequently cause 100%+ swings in local clinic visits."
        },
        {
          id: "l2",
          label: "Aliens beamed radiation onto the valley.",
          explanation: "Absurd non-parsimonious conjecture."
        },
        {
          id: "l3",
          label: "All the patients moved into town on the exact same weekend.",
          explanation: "Implausible demographic hypothesis without records."
        }
      ],
      correctAlternativeId: "l1",

      weakenersOptions: [
        {
          id: "w1",
          label: "State environmental protection air-monitors throughout the town showing zero detectable volatile organic compounds, coupled with county health data revealing neighboring towns without factories had an identical 120% respiratory surge.",
          explanation: "Shows both lack of toxic exposure and presence of a broader regional outbreak independent of the factory."
        },
        {
          id: "w2",
          label: "The factory donating ten thousand dollars to the local high school soccer team.",
          explanation: "Philanthropy does not alter chemical emissions or physical health impacts."
        },
        {
          id: "w3",
          label: "A council member stating they like the factory's exterior paint color.",
          explanation: "Aesthetic preference is irrelevant."
        }
      ],
      correctWeakenerId: "w1",

      verdictOptions: [
        {
          id: "well-supported",
          label: "Well supported",
          rationale: "Requires demonstrated chemical exposure and controlled epidemiological analysis, which is absent."
        },
        {
          id: "plausible-unestablished",
          label: "Plausible but unestablished",
          rationale: "While industrial pollution can cause respiratory illness, asserting it as 'undeniable' based solely on unadjusted clinic visits is premature."
        },
        {
          id: "insufficient-info",
          label: "Insufficient information",
          rationale: "Correct epistemic stance: the correlation warrants an urgent environmental investigation, but available data is fundamentally insufficient to confirm or deny factory causation."
        },
        {
          id: "not-established",
          label: "Not established",
          rationale: "While acceptable, 'insufficient information' is even more precise because critical environmental assays are pending."
        },
        {
          id: "contradicted",
          label: "Contradicted by evidence",
          rationale: "We do not have data proving the factory is harmless, so we cannot claim contradiction."
        }
      ],
      correctVerdictId: "insufficient-info"
    },
    expertSynthesis: {
      summary: "A real spike in clinic visits coincident with a factory opening is an urgent diagnostic signal that justifies immediate environmental monitoring, but does not prove the plant is the poisoner.",
      whyInsufficientInfoOrNotEstablished: "This case illustrates the vital discipline of 'insufficient information'. Refusing to conclude 'the factory did it' is not corporate apologetics—it prevents the town from missing a deadly mold bloom or virulent respiratory pathogen while chasing a premature conclusion.",
      plausibleAlternativesDiscussion: "Epidemiological clusters frequently evaporate upon scrutiny: if three neighboring valleys without factories saw the exact same 115% surge due to an early RSV wave, the factory's involvement drops to baseline."
    }
  },

  {
    id: "viral-screenshot",
    title: "The Viral Press Release Screenshot",
    scenario: "An image circulating on social media shows what appears to be a cropped PDF press release from the 'International Ocean Conservancy' announcing the immediate ban of all coastal recreational fishing worldwide.",
    quote: "\"URGENT: International Ocean Conservancy mandates total global prohibition on recreational fishing effective November 1st. Confirmed screenshot inside!\"",
    sourceContext: "A widely reposted image on an image-sharing network, cropped closely around headline text with no verifiable URL, cryptographic signature, or official press kit link.",
    claimType: "factual",
    typeExplanation: "It asserts that a specific international body has enacted a legal prohibition, a factual institutional event.",
    steps: {
      assertionOptions: [
        {
          id: "a1",
          label: "The International Ocean Conservancy has officially enacted a legally binding worldwide ban on recreational fishing taking effect this November.",
          explanation: "Directly mirrors the viral post's factual assertion."
        },
        {
          id: "a2",
          label: "Fish populations are declining rapidly in all oceans.",
          explanation: "Broader biological topic, not the specific administrative claim made in the viral post."
        },
        {
          id: "a3",
          label: "A graphic designer used Photoshop to test fonts.",
          explanation: "A possible hypothesis of origin, but not what the post itself asserts."
        }
      ],
      correctAssertionId: "a1",

      truthConditions: [
        {
          id: "t1",
          label: "An official press release published on the verified domain of the organization, coverage by reputable journalistic wire services, and treaty/statutory legal authority to enact such bans.",
          explanation: "Authentic international institutional mandates leave clear administrative, legal, and verifiable paper trails on primary domains."
        },
        {
          id: "t2",
          label: "Checking whether the screenshot has received more than 50,000 shares.",
          explanation: "Share volume measures social virality, not factual authenticity."
        },
        {
          id: "t3",
          label: "Interviewing five recreational fishermen about whether they feel angry.",
          explanation: "Emotional reactions do not establish whether an administrative policy was actually decreed."
        }
      ],
      correctTruthConditionId: "t1",

      evidenceOffered: [
        {
          id: "e1",
          label: "A cropped, low-resolution static image file showing text and a logo without an intact header, digital signature, or direct web link.",
          explanation: "The sole piece of evidence is an easily fabricated visual artifact."
        },
        {
          id: "e2",
          label: "A United Nations treaty ratified by 140 countries.",
          explanation: "No treaty is cited or linked."
        },
        {
          id: "e3",
          label: "A live video press conference broadcast on international television.",
          explanation: "No broadcast exists."
        }
      ],
      correctEvidenceId: "e1",

      reliabilityOptions: [
        {
          id: "r1",
          label: "Near zero: digital images of text are trivial to forge in minutes via browser developer tools or image editors, and non-governmental charities lack legal authority to mandate global bans.",
          explanation: "Correctly identifies both technical ease of forgery and institutional implausibility."
        },
        {
          id: "r2",
          label: "High: modern smartphones watermark images automatically, preventing misinformation.",
          explanation: "Completely false; smartphones do not verify truth or prevent screenshot manipulation."
        },
        {
          id: "r3",
          label: "Moderate: people rarely take the time to create fake documents about recreational fishing.",
          explanation: "False: online rage-bait targeted at hobbyists is one of the commonest engagement-farming strategies."
        }
      ],
      correctReliabilityId: "r1",

      assumptionsOptions: [
        {
          id: "m1",
          label: "Assuming an NGO has sovereign regulatory police powers over all global territorial waters, that a cropped image is authentic, and that no news outlets reporting on it is normal.",
          explanation: "Exposes the institutional absurdity and media absence required for the claim to hold."
        },
        {
          id: "m2",
          label: "Assuming fish prefer worms over artificial lures.",
          explanation: "Irrelevant angling trivia."
        },
        {
          id: "m3",
          label: "Assuming the reader has an active fishing license.",
          explanation: "Irrelevant personal status."
        }
      ],
      correctAssumptionId: "m1",

      alternativesOptions: [
        {
          id: "l1",
          label: "Fabricated rage-bait created for engagement/clicks, a satire piece shared out of context, an edited draft proposal never adopted, or a routine advisory report mischaracterized as a 'mandatory ban'.",
          explanation: "Standard mechanisms behind viral bureaucratic falsehoods."
        },
        {
          id: "l2",
          label: "A secret military operation coded under marine biology terminology.",
          explanation: "Unsubstantiated conspiracy thinking."
        },
        {
          id: "l3",
          label: "The screenshot was generated by a malfunctioning microwave.",
          explanation: "Nonsensical."
        }
      ],
      correctAlternativeId: "l1",

      weakenersOptions: [
        {
          id: "w1",
          label: "Navigating to the organization's official website finding a prominent alert: 'Beware of fake screenshots circulating on social media claiming recreational fishing bans; we have issued no such statement.'",
          explanation: "Direct primary-source refutation from the purported issuing body."
        },
        {
          id: "w2",
          label: "Someone commenting 'Fake news!' in the thread without a link.",
          explanation: "Unsubstantiated comment assertion carries little evidential weight by itself."
        },
        {
          id: "w3",
          label: "The post having grammatical typos in the comments section.",
          explanation: "Comment typos do not assess the primary source."
        }
      ],
      correctWeakenerId: "w1",

      verdictOptions: [
        {
          id: "well-supported",
          label: "Well supported",
          rationale: "Completely unfounded."
        },
        {
          id: "plausible-unestablished",
          label: "Plausible but unestablished",
          rationale: "The claim is institutionally implausible since NGOs cannot mandate global sovereign bans."
        },
        {
          id: "not-established",
          label: "Not established",
          rationale: "A lone unverified screenshot cannot establish institutional decrees."
        },
        {
          id: "contradicted",
          label: "Contradicted by evidence",
          rationale: "When primary official sources show no record, and the entity lacks legal mandate, the claim is contradicted by institutional reality."
        },
        {
          id: "insufficient-info",
          label: "Insufficient information",
          rationale: "We have ample knowledge regarding jurisdictional power of NGOs and ease of screenshot fabrication to dismiss reliance."
        }
      ],
      correctVerdictId: "not-established"
    },
    expertSynthesis: {
      summary: "Screenshots of text are among the lowest-grade forms of evidence on the modern internet. Anyone can edit HTML in a browser inspector in ten seconds.",
      whyInsufficientInfoOrNotEstablished: "Evaluating evidence requires asking: 'Does this channel have the authority and authenticity to validate what is asserted?' An NGO cannot enact worldwide bans; even if authentic, it would be an advisory recommendation, not an enforceable mandate.",
      plausibleAlternativesDiscussion: "Engagement farming relies on manufactured grievance: threatening a beloved leisure activity (fishing) triggers instant emotional sharing before users verify the primary URL."
    }
  },

  {
    id: "investment-record",
    title: "The 'Undefeated' Financial Trading Record",
    scenario: "An online financial influencer advertises a paid subscription channel boasting an unbroken series of winning equity swing trades.",
    quote: "\"Our proprietary momentum algorithm has generated 48 consecutive winning trades over the past 9 months with zero losing sessions. Don't miss the next entry.\"",
    sourceContext: "A promotional landing page and messaging channel with cropped account balance graphics and client testimonials.",
    claimType: "predictive",
    typeExplanation: "While reporting historical performance, its pragmatic communicative force is predictive: it promises that subscribing will yield superior future market returns.",
    steps: {
      assertionOptions: [
        {
          id: "a1",
          label: "The stock market has gone up every day for nine months.",
          explanation: "Inaccurate: the claim is about the trader's selective portfolio, not the entire macro economy."
        },
        {
          id: "a2",
          label: "The operator's strategy achieved 48 profitable exits in a row under real market conditions, proving persistent algorithmic predictive superiority without hidden liabilities.",
          explanation: "Captures both the explicit metric and the implicit assertion of reproducible market edge."
        },
        {
          id: "a3",
          label: "The influencer works at a registered commercial bank.",
          explanation: "Never asserted in the text."
        }
      ],
      correctAssertionId: "a2",

      truthConditions: [
        {
          id: "t1",
          label: "An independently audited, third-party broker ledger showing all open and closed trades with entry timestamps, slippage, and verifying no massive unrealized underwater drawdowns.",
          explanation: "Professional financial verification requires complete ledger accounting, not self-selected screenshots."
        },
        {
          id: "t2",
          label: "Checking if the influencer wears expensive designer watches in videos.",
          explanation: "Luxury displays are standard props used in financial marketing funnels."
        },
        {
          id: "t3",
          label: "Asking five subscribers if they feel enthusiastic about the channel.",
          explanation: "Early enthusiasm is common in speculative bubbles before inevitable drawdowns."
        }
      ],
      correctTruthConditionId: "t1",

      evidenceOffered: [
        {
          id: "e1",
          label: "Self-published screenshots of winning ticket summaries and cherry-picked chat screenshots without broker audit or tax filings.",
          explanation: "The evidence consists entirely of vendor-curated, easily faked marketing snapshots."
        },
        {
          id: "e2",
          label: "Audited SEC regulatory filings from a licensed custodian.",
          explanation: "No regulatory custody statements are provided."
        },
        {
          id: "e3",
          label: "A Nobel prize in economic sciences.",
          explanation: "Irrelevant and absent."
        }
      ],
      correctEvidenceId: "e1",

      reliabilityOptions: [
        {
          id: "r1",
          label: "Severely compromised: overwhelming commercial conflict of interest, survivorship bias, cherry-picking, demo paper accounts posing as live funds, and hiding massive open paper losses.",
          explanation: "Directly outlines the well-known mechanisms of financial marketing deception."
        },
        {
          id: "r2",
          label: "High: 48 trades is a statistically significant sample size.",
          explanation: "Sample size is meaningless if the data is filtered after the fact (the Texas sharpshooter fallacy)."
        },
        {
          id: "r3",
          label: "Moderate: the channel would be shut down by police if it wasn't true.",
          explanation: "Unregulated social trading channels frequently operate across borders without immediate regulatory intervention."
        }
      ],
      correctReliabilityId: "r1",

      assumptionsOptions: [
        {
          id: "m1",
          label: "Assuming all executed trades were published in advance, that no losing positions remain open and uncounted, that paper trading was not used, and that past run will continue.",
          explanation: "Identifies the classic martingale or cherry-picking tricks where losers are simply held indefinitely."
        },
        {
          id: "m2",
          label: "Assuming the New York Stock Exchange is located on Wall Street.",
          explanation: "Trivial geographic fact."
        },
        {
          id: "m3",
          label: "Assuming every subscriber uses a high-speed fiber-optic connection.",
          explanation: "Technical peripheral detail."
        }
      ],
      correctAssumptionId: "m1",

      alternativesOptions: [
        {
          id: "l1",
          label: "Survivorship bias (running multiple parallel accounts and only showing the one that won by random chance), refusing to close losing trades to claim 'zero losses', editing demo account HTML, or plain fabrication.",
          explanation: "Standard, trivial ways anyone can produce an apparent 'undefeated' record."
        },
        {
          id: "l2",
          label: "The operator has a working time machine.",
          explanation: "Physically impossible alternative."
        },
        {
          id: "l3",
          label: "The Federal Reserve secretly directs all trading signals to this single influencer.",
          explanation: "Absurd conspiracy theory."
        }
      ],
      correctAlternativeId: "l1",

      weakenersOptions: [
        {
          id: "w1",
          label: "Subpoenaed broker records showing three open accounts that went completely bankrupt while only the winning branch was displayed publicly.",
          explanation: "Conclusively demonstrates deliberate survivorship bias and fraud."
        },
        {
          id: "w2",
          label: "A day where the stock market drops 1% in value.",
          explanation: "A routine market down-day does not prove whether the trader was short or hedged."
        },
        {
          id: "w3",
          label: "The trader changing the channel's profile picture.",
          explanation: "Cosmetic change with zero epistemic significance."
        }
      ],
      correctWeakenerId: "w1",

      verdictOptions: [
        {
          id: "well-supported",
          label: "Well supported",
          rationale: "Unsubstantiated marketing claim."
        },
        {
          id: "plausible-unestablished",
          label: "Plausible but unestablished",
          rationale: "An undefeated 48-trade record in volatile markets without drawdowns is mathematically near-impossible without deceptive bookkeeping."
        },
        {
          id: "not-established",
          label: "Not established",
          rationale: "Lacks the audited third-party ledger required to substantiate claims of extraordinary market edge."
        },
        {
          id: "insufficient-info",
          label: "Insufficient information",
          rationale: "We lack third-party broker records, making independent verification impossible."
        },
        {
          id: "contradicted",
          label: "Contradicted by evidence",
          rationale: "Requires subpoenaed books to prove outright contradiction."
        }
      ],
      correctVerdictId: "not-established"
    },
    expertSynthesis: {
      summary: "In probability and finance, '48 consecutive wins with zero losses' is a massive red flag for survivorship bias, cherry-picking, or hiding catastrophic unrealized losses in unclosed positions.",
      whyInsufficientInfoOrNotEstablished: "The absence of verified audited statements means this claim fails the basic threshold of justification. Never accept self-selected screenshots as proof of statistical superiority.",
      plausibleAlternativesDiscussion: "The classic split-funnel trick: send 500 people a 'buy' tip and 500 a 'sell' tip. Whichever side wins, repeat the split. After 8 rounds, a small cohort thinks you possess infallible clairvoyance, while you quietly discard the hundreds who lost everything."
    }
  },

  {
    id: "sleep-gadget",
    title: "The Magnetic Bio-Resonance Sleep Band",
    scenario: "A wellness tech company markets an elastic wristband embedded with micro-magnets, claiming it realigns circadian rhythms to cure insomnia in seven nights.",
    quote: "\"Our patented pulsed bio-magnetic resonance aligns cellular polarity to restore deep delta-wave sleep within seven nights guaranteed, backed by 4,000 five-star reviews!\"",
    sourceContext: "An ecommerce direct-to-consumer sales page with polished 3D product renders and customer testimonials.",
    claimType: "factual",
    typeExplanation: "It claims specific biophysical mechanisms ('cellular polarity', 'delta-wave sleep') that result in clinical remediation of insomnia within a fixed timeline.",
    steps: {
      assertionOptions: [
        {
          id: "a1",
          label: "Wearing this wristband physically modulates brainwave frequencies via magnetic resonance to cure clinical sleep disorders in seven days.",
          explanation: "Accurate representation of the physiological and medical claim asserted."
        },
        {
          id: "a2",
          label: "Customers prefer soft wristbands over metal watches.",
          explanation: "Trivial comfort preference, ignoring the medical efficacy claim."
        },
        {
          id: "a3",
          label: "Sleep is important for human biological survival.",
          explanation: "General biological truism that evades the specific product's therapeutic assertion."
        }
      ],
      correctAssertionId: "a1",

      truthConditions: [
        {
          id: "t1",
          label: "Controlled polysomnography (clinical sleep lab EEG) trials comparing active bands against identical sham/placebo bands with non-magnetic dummy inserts, showing statistically significant increases in stage 3 delta sleep.",
          explanation: "The gold-standard empirical test for sleep architecture and bio-electric claims."
        },
        {
          id: "t2",
          label: "Checking whether the company's customer service responds quickly to emails.",
          explanation: "Customer support speed is not a biological or therapeutic truth condition."
        },
        {
          id: "t3",
          label: "Verifying that the magnets stick to a kitchen refrigerator door.",
          explanation: "Proves magnetism exists, but does not prove magnetic realignment of human cellular polarity or delta sleep induction."
        }
      ],
      correctTruthConditionId: "t1",

      evidenceOffered: [
        {
          id: "e1",
          label: "Customer reviews on the company's own sales platform and pseudo-scientific terminology ('bio-magnetic cellular resonance').",
          explanation: "Uncurated user ratings on a vendor website and jargon do not constitute scientific clinical evidence."
        },
        {
          id: "e2",
          label: "Replicated peer-reviewed randomized controlled trials in respected sleep medicine journals.",
          explanation: "No peer-reviewed medical trials are presented."
        },
        {
          id: "e3",
          label: "FDA clinical approval as a class III medical device.",
          explanation: "No medical device certification is held."
        }
      ],
      correctEvidenceId: "e1",

      reliabilityOptions: [
        {
          id: "r1",
          label: "Extremely poor: on-site reviews can be filtered or purchased, subjective sleep satisfaction is notoriously vulnerable to placebo effects, and static low-strength magnets cannot penetrate neural tissue to alter delta waves.",
          explanation: "Integrates biophysical impossibility with well-documented ecommerce review gaming."
        },
        {
          id: "r2",
          label: "Very high: four thousand people cannot all be mistaken about their own sleep.",
          explanation: "Ignores the massive power of the placebo effect, review manipulation, and natural regression to the mean."
        },
        {
          id: "r3",
          label: "Moderate: magnets are used in hospital MRI machines, so magnetism must affect cells.",
          explanation: "Equivocation: an MRI uses superconducting multi-tesla electromagnets, which is billions of times stronger than a tiny wristband magnet."
        }
      ],
      correctReliabilityId: "r1",

      assumptionsOptions: [
        {
          id: "m1",
          label: "Assuming weak static magnetic fields can alter cerebral brainwaves from the wrist, that review aggregators reflect unbiased real experiences, and that insomnia has a single physical cause solved in 7 days.",
          explanation: "Unpacks the physiological, methodological, and clinical assumptions."
        },
        {
          id: "m2",
          label: "Assuming people sleep in darkened rooms.",
          explanation: "Environmental factor, but not the core epistemic assumption behind the gadget's mechanism."
        },
        {
          id: "m3",
          label: "Assuming the wristband comes in three sizes.",
          explanation: "Irrelevant product inventory detail."
        }
      ],
      correctAssumptionId: "m1",

      alternativesOptions: [
        {
          id: "l1",
          label: "Placebo effect, regression to the mean (people buy sleep gadgets when insomnia is at its peak, so any subsequent natural recovery is credited to the device), fake/curated reviews, and simultaneous lifestyle adjustments (e.g. going to bed earlier).",
          explanation: "Standard scientific explanations for apparent wellness product successes."
        },
        {
          id: "l2",
          label: "The wristband emits psychic sleep frequencies.",
          explanation: "Supernatural speculation without physical basis."
        },
        {
          id: "l3",
          label: "Customers are hypnotized through the computer screen.",
          explanation: "Implausible conjecture."
        }
      ],
      correctAlternativeId: "l1",

      weakenersOptions: [
        {
          id: "w1",
          label: "A rigorous double-blind study where participants wearing inert silicone bands slept identically to those wearing the magnetic band, with neither showing EEG delta-wave changes.",
          explanation: "Directly falsifies the claim of specific bio-magnetic therapeutic efficacy."
        },
        {
          id: "w2",
          label: "A customer review complaining about shipping delays.",
          explanation: "Logistics complaints do not evaluate medical efficacy."
        },
        {
          id: "w3",
          label: "The band being available only in black.",
          explanation: "Aesthetic limitation of no evidentiary relevance."
        }
      ],
      correctWeakenerId: "w1",

      verdictOptions: [
        {
          id: "well-supported",
          label: "Well supported",
          rationale: "No empirical evidence supports static magnetic delta-wave modulation."
        },
        {
          id: "plausible-unestablished",
          label: "Plausible but unestablished",
          rationale: "The proposed biophysical mechanism violates established neurophysics."
        },
        {
          id: "not-established",
          label: "Not established",
          rationale: "The promotional claims rest entirely on testimonials and scientific-sounding jargon without clinical verification."
        },
        {
          id: "insufficient-info",
          label: "Insufficient information",
          rationale: "While a clinical trial has not been run on this specific brand, the known physics of static magnets makes the claim decisively unestablished."
        },
        {
          id: "contradicted",
          label: "Contradicted by evidence",
          rationale: "Known biophysics and neuroscience already contradict the notion that wrist magnets modulate cortical delta oscillations."
        }
      ],
      correctVerdictId: "not-established"
    },
    expertSynthesis: {
      summary: "Scientific jargon ('bio-magnetic cellular polarity') is routinely deployed to disguise inert consumer items as cutting-edge medical breakthroughs.",
      whyInsufficientInfoOrNotEstablished: "Notice how testimonials substitute for evidence: 4,000 positive reviews tell us people were happy or experienced a placebo lift, but tell us zero about whether the wristband physically altered brainwaves.",
      plausibleAlternativesDiscussion: "Regression to the mean is the greatest friend of alternative health products: people seek remedies when their distress is at an acute maximum. When the condition naturally ebbs back to normal baseline, the human brain erroneously credits whatever talisman they purchased."
    }
  },

  {
    id: "ancient-remedy",
    title: "The 'Ancient Mountain Herb' Longevity Elixir",
    scenario: "A wellness brand promotes a dried root extract harvested from a remote mountain range, claiming it was used by a centenarian tribe to prevent cellular aging.",
    quote: "\"For four thousand years, the mountain elders used Root X to prevent all metabolic disease and live past 100. Modern science is finally catching up to ancient wisdom!\"",
    sourceContext: "An online sales article featuring evocative photographs of misty mountain summits and smiling elderly villagers.",
    claimType: "interpretive",
    typeExplanation: "It interprets traditional cultural lore as clinical medical validation, while making an underlying historical and biological assertion.",
    steps: {
      assertionOptions: [
        {
          id: "a1",
          label: "Consuming this specific herb prevents all metabolic diseases and causes humans to live beyond a century, as verified by 4,000 years of unbroken historical practice.",
          explanation: "Accurately frames the historical pedigree and medical guarantee stated in the marketing."
        },
        {
          id: "a2",
          label: "People in mountain villages enjoy drinking herbal tea.",
          explanation: "Trivial social custom that erases the medical and historical efficacy assertion."
        },
        {
          id: "a3",
          label: "Mountains are older than valleys.",
          explanation: "Geological truism with no relevance to the health claim."
        }
      ],
      correctAssertionId: "a1",

      truthConditions: [
        {
          id: "t1",
          label: "Verifiable archaeological and historical records confirming centuries of uniform usage, accurate demographic birth registries proving extraordinary longevity, and modern biochemical isolation showing anti-aging mechanisms in human trials.",
          explanation: "Tests both the historical longevity claim (demography) and the pharmacological anti-aging assertion."
        },
        {
          id: "t2",
          label: "Checking whether the packaging looks vintage and authentic.",
          explanation: "Graphic design choices have zero evidentiary value."
        },
        {
          id: "t3",
          label: "Finding a travel blogger who visited the mountain region.",
          explanation: "Tourism anecdotes do not substitute for rigorous historical demography or clinical biochemistry."
        }
      ],
      correctTruthConditionId: "t1",

      evidenceOffered: [
        {
          id: "e1",
          label: "Appeals to tradition, romanticized folkloric mythology, and photographic images of elderly mountain dwellers without verifiable birth records or controlled health data.",
          explanation: "Classic appeal to antiquity (argumentum ad antiquitatem) combined with visual framing."
        },
        {
          id: "e2",
          label: "Longitudinal cohort studies tracking 10,000 subjects over 50 years.",
          explanation: "No long-term epidemiological tracking is offered."
        },
        {
          id: "e3",
          label: "Cellular senescence assays published in Nature or Science.",
          explanation: "No such research is presented."
        }
      ],
      correctEvidenceId: "e1",

      reliabilityOptions: [
        {
          id: "r1",
          label: "Extremely low: appeals to antiquity commit a classic fallacy; ancient longevity myths frequently stem from absent birth registries, high infant mortality masking demographic averages, and severe survivorship bias.",
          explanation: "Deconstructs the appeal to tradition and common historical demography errors."
        },
        {
          id: "r2",
          label: "Very high: if an herb was used for 4,000 years, it must work, otherwise people would have stopped using it.",
          explanation: "Commits the appeal to tradition: bloodletting was practiced for over two thousand years despite being actively harmful."
        },
        {
          id: "r3",
          label: "Moderate: ancient healers understood holistic energy better than reductionist laboratory medicine.",
          explanation: "Romanticized nostalgia without empirical or biochemical validation."
        }
      ],
      correctReliabilityId: "r1",

      assumptionsOptions: [
        {
          id: "m1",
          label: "Assuming old beliefs are inherently superior to tested medicine, that remote folklore reflects accurate pharmacological truth, and that mountain villagers lived to 100 solely because of one root rather than diet, physical labor, or genetics.",
          explanation: "Unpacks the appeal to antiquity, monocausal reductionism, and romanticized ethnography."
        },
        {
          id: "m2",
          label: "Assuming mountains receive more rainfall than coastal plains.",
          explanation: "Meteorological detail unrelated to the core argument."
        },
        {
          id: "m3",
          label: "Assuming herbs can only be boiled in water.",
          explanation: "Culinary preparation detail."
        }
      ],
      correctAssumptionId: "m1",

      alternativesOptions: [
        {
          id: "l1",
          label: "The 'centenarian' narrative is modern marketing mythology; actual historical lifespans were shortened by infections; any real longevity in rural communities stems from lifelong physical activity, low smoking rates, social cohesion, or simple demographic record inflation.",
          explanation: "Considers well-documented anthropological and epidemiological realities of purported 'longevity zones'."
        },
        {
          id: "l2",
          label: "The plant was originally brought from another planet.",
          explanation: "Baseless sci-fi conjecture."
        },
        {
          id: "l3",
          label: "The villagers are all undercover actors hired in 1840.",
          explanation: "Absurd conspiracy theory."
        }
      ],
      correctAlternativeId: "l1",

      weakenersOptions: [
        {
          id: "w1",
          label: "Historical demographic census records from the region revealing an average historical life expectancy of 42 years, coupled with biochemical analysis showing Root X contains nothing more than common cellulose and trace minerals.",
          explanation: "Directly refutes both the longevity myth and the pharmacological uniqueness of the root."
        },
        {
          id: "w2",
          label: "A customer review stating the tea tastes somewhat bitter.",
          explanation: "Taste profile is irrelevant to longevity claims."
        },
        {
          id: "w3",
          label: "The company offering a 10% discount coupon.",
          explanation: "Commercial discounting has no evidentiary bearing."
        }
      ],
      correctWeakenerId: "w1",

      verdictOptions: [
        {
          id: "well-supported",
          label: "Well supported",
          rationale: "Entirely unsupported by controlled clinical or demographic data."
        },
        {
          id: "plausible-unestablished",
          label: "Plausible but unestablished",
          rationale: "While plants can yield active alkaloids, the claim of guaranteed centenarian life via 4,000-year wisdom is thoroughly unestablished."
        },
        {
          id: "not-established",
          label: "Not established",
          rationale: "Relies on the appeal-to-tradition fallacy and romanticized folklore without clinical or demographic verification."
        },
        {
          id: "insufficient-info",
          label: "Insufficient information",
          rationale: "We already have sufficient anthropological and logical grounds to dismiss the folklore argument as an unestablished marketing claim."
        },
        {
          id: "contradicted",
          label: "Contradicted by evidence",
          rationale: "General human history contradicts universal 4,000-year disease-free centenarians."
        }
      ],
      correctVerdictId: "not-established"
    },
    expertSynthesis: {
      summary: "The claim relies on the 'appeal to tradition'—the fallacious presumption that because an idea is perceived as ancient, it must be effective, safe, and enlightened.",
      whyInsufficientInfoOrNotEstablished: "Ancient practices persisted for millennia without being effective: bloodletting, trepanation, and mercury tonics were celebrated for centuries despite causing immense harm. Time elapsed does not equal empirical validation.",
      plausibleAlternativesDiscussion: "When longevity in remote populations has been systematically investigated by demographers, purported clusters of supercentenarians frequently turn out to be clerical pension errors, missing baptismal records, or conflation of deceased parents with surviving children of the same name."
    }
  }
];
